import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, getDocs, collection, updateDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import ItemCard from "../components/ItemCard/ItemCard";
import Spinner from "../components/Spinner/Spinner";
import Button from "../components/Button/Button";
import Chip from "../components/Chip/Chip";
import HorizontalScroller from "../components/HorizontalScroller/HorizontalScroller";

export default function PublicListView() {
    const { listId } = useParams();
    const [list, setList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [members, setMembers] = useState([]);
    const [activeMember, setActiveMember] = useState("All");
    const memberRefs = useRef({});
    const [usersMap, setUsersMap] = useState({});

    useEffect(() => {
        const fetchList = async () => {
            try {
                const docRef = doc(db, "lists", listId);
                const docSnap = await getDoc(docRef);

                if (!docSnap.exists()) {
                    console.error("No such list!");
                    setList(null);
                    return;
                }

                const listData = { id: docSnap.id, ...docSnap.data() };
                setList(listData);

                // Fetch all users
                const usersSnap = await getDocs(collection(db, "users"));
                const allUsers = usersSnap.docs.map((doc) => ({
                    uid: doc.id,
                    ...doc.data(),
                }));

                // Build map of uid → display name
                const memberNames = (listData.members || []).reduce((acc, memberUid) => {
                    const memberInfo =
                        listData.memberInfo?.find((m) => m.uid === memberUid) ||
                        allUsers.find((u) => u.uid === memberUid) ||
                        {};

                    const fullName =
                        memberInfo.firstName && memberInfo.lastName
                            ? `${memberInfo.firstName} ${memberInfo.lastName}`
                            : memberInfo.displayName ||
                            memberInfo.name ||
                            "Member";

                    acc[memberUid] = fullName;
                    return acc;
                }, {});

                setUsersMap(memberNames);

                // Build itemsByMember mapping
                const itemsByMember = {};
                (listData.items || []).forEach((item) => {
                    const uid = item.addedBy?.uid;
                    const name = memberNames[uid] || "Member";
                    if (!itemsByMember[name]) itemsByMember[name] = [];
                    itemsByMember[name].push(item);
                });

                // Build generic member list (items OR interests)
                const memberLists = Array.from(
                    new Set(
                        (listData.members || [])
                            .map((uid) => memberNames[uid])
                            .filter(Boolean)
                    )
                ).filter((name) => {
                    const hasItems = (itemsByMember[name] || []).length > 0;
                    const memberUid = Object.keys(memberNames).find((uid) => memberNames[uid] === name);
                    const memberInfo = listData.memberInfo?.find((m) => m.uid === memberUid);
                    const hasInterests = (memberInfo?.interests || []).length > 0;
                    return hasItems || hasInterests;
                });

                // Sort alphabetically
                memberLists.sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" }));

                setMembers(memberLists);

                // Build refs
                const refs = { All: null };
                memberLists.forEach((name) => (refs[name] = null));
                memberRefs.current = refs;
            } catch (err) {
                console.error("Error fetching public list:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchList();
    }, [listId]);

    const scrollToMember = (memberName) => {
        const ref = memberRefs.current[memberName];
        if (memberName === "All") {
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        if (ref) {
            const headerOffset = 230;
            const elementPosition = ref.getBoundingClientRect().top + window.scrollY;
            const offsetPosition = elementPosition - headerOffset;
            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth",
            });
        }
    };

    const handleMemberClick = (memberName) => {
        setActiveMember(memberName);
        scrollToMember(memberName);
    };

    const handleMarkAsPurchased = async (item, purchaserName) => {
        if (!list) return;

        const updatedItems = (list.items || []).map((i) => {
            if (i.id === item.id) {
                const purchasedBy = Array.isArray(i.purchasedBy) ? i.purchasedBy : [];
                if (!purchaserName) {
                    return { ...i, purchasedBy: [] };
                } else {
                    return {
                        ...i,
                        purchasedBy: purchasedBy.includes(purchaserName)
                            ? purchasedBy
                            : [...purchasedBy, purchaserName],
                    };
                }
            }
            return i;
        });

        const listRef = doc(db, "lists", list.id);
        await updateDoc(listRef, { items: updatedItems });
        setList((prev) => ({ ...prev, items: updatedItems }));
    };

    if (loading) return <Spinner />;
    if (!list) return <p className="text-center mt-12">List not found</p>;

    const itemsByMember = {};
    (list.items || []).forEach((item) => {
        const uid = item.addedBy?.uid;
        const name = usersMap[uid] || "Member";
        if (!itemsByMember[name]) itemsByMember[name] = [];
        itemsByMember[name].push(item);
    });

    return (
        <div className="max-w-3xl mx-auto px-6 md:px-0">
            {/* Fixed Header Section */}
            <div className="fixed top-0 pt-[64px] left-0 right-0 bg-white z-20">
                <div className="max-w-3xl mx-auto px-6 md:px-0 pt-10">
                    <h1 className="text-xl font-bold">{list.name}</h1>
                </div>

                <HorizontalScroller className="mt-2 max-w-3xl mx-auto">
                    <Button
                        variant={activeMember === "All" ? "primary" : "secondary"}
                        onClick={() => handleMemberClick("All")}
                        className="border-2 border-primary flex-shrink-0"
                    >
                        All
                    </Button>

                    {members.map((member) => (
                        <Button
                            key={member}
                            variant={activeMember === member ? "primary" : "secondary"}
                            onClick={() => handleMemberClick(member)}
                            className="border-2 border-primary flex-shrink-0"
                        >
                            {member}
                        </Button>
                    ))}
                </HorizontalScroller>
            </div>

            {/* Main Content */}
            <div ref={(el) => (memberRefs.current["All"] = el)} className="pt-48">
                <div className="space-y-12">
                    {members.map((member) => (
                        <section key={member} ref={(el) => (memberRefs.current[member] = el)}>
                            <h2 className="text-xl font-semibold mb-2">{member}'s Wish List</h2>

                            {/* Interests */}
                            {(() => {
                                const memberUid = Object.keys(usersMap).find(
                                    (uid) => usersMap[uid] === member
                                );
                                const memberInfo = list.memberInfo?.find((m) => m.uid === memberUid);
                                const interests = memberInfo?.interests || [];

                                return interests.length > 0 ? (
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        <span className="font-medium">Interests:</span>
                                        {interests.map((interest, index) => (
                                            <Chip
                                                key={index}
                                                label={interest}
                                                showRemove={false}
                                                disabled={true}
                                            />
                                        ))}
                                    </div>
                                ) : null;
                            })()}

                            {/* Items */}
                            <div className="flex flex-wrap gap-4">
                                {(itemsByMember[member] || []).map((item) => (
                                    <div
                                        key={item.id || item.name}
                                        className="flex-1 min-w-[200px] max-w-[calc((100%/3)-1rem)]"
                                    >
                                        <ItemCard
                                            item={item}
                                            showPurchaseButton={!list.isSecretSanta}
                                            onPurchase={(purchaserName) =>
                                                handleMarkAsPurchased(item, purchaserName)
                                            }
                                            publicView={true}
                                        />
                                    </div>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
}
